import { cn } from '@/lib/utils'
import { InfoPopover } from '@/components/InfoPopover'
import { SortHeader } from '@/components/ui/sort-header'
import { useSortable } from '@/lib/useSortable'
import type { CreditByClient, CreditClient } from '../types'
import { MONO, fmt, monthLabel } from './format'

const CARD = 'rounded-2xl border border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900'

const valueOf = (c: CreditClient, key: string): number | string => {
  if (key === 'clinic') return c.clinic
  if (key === 'fee') return c.fee || 0
  if (key === 'total') return c.total
  if (key[0] === 'm') return c.monthly[Number(key.slice(1))] || 0 // "m0", "m1", … a month column
  return 0
}

/** PC credit consumed per clinic over the window (the gateway figure — the true
 *  total used). Every column is click-to-sort (default: Total used ↓); monthly
 *  columns scroll horizontally. */
export function CreditByClientCard({ data, currency }: { data: CreditByClient; currency: string }) {
  const { sorted: clinics, sortKey, sortDir, toggle } = useSortable(data.clinics, valueOf, { key: 'total', dir: 'desc' })
  const grand = clinics.reduce((t, c) => t + c.total, 0)
  const monthTotal = (mi: number) => clinics.reduce((t, c) => t + (c.monthly[mi] || 0), 0)
  const empty = data.months.length === 0

  return (
    <div className={CARD}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-5 pt-5">
        <h3 className="inline-flex items-center gap-1 text-sm font-semibold text-stone-800 dark:text-stone-100">
          PC credit by client
          <InfoPopover term="pcCreditUsed" />
        </h3>
        <span className="text-[11px] text-stone-400 dark:text-stone-500">
          {empty
            ? 'no PC credit in this range'
            : `${clinics.length} clinics · ${monthLabel(data.months[0])} – ${monthLabel(data.months[data.months.length - 1])} · gateway draw-down`}
        </span>
      </div>

      {empty && (
        <p className="px-5 pb-5 pt-4 text-sm text-stone-400 dark:text-stone-500">
          PC credit tracking starts Jun 2025 — widen the range or move the month forward to see draw-down.
        </p>
      )}
      {!empty && (
        <>

      <div className="mt-3 overflow-auto" style={{ maxHeight: 480 }}>
        <table className="w-full min-w-[760px] border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-white dark:bg-stone-900">
            <tr className="border-y border-stone-100 text-xs text-stone-400 dark:border-stone-800 dark:text-stone-500">
              <th className="sticky left-0 z-20 bg-white px-5 py-2 text-left font-medium dark:bg-stone-900">
                <SortHeader label="Clinic" sortKey="clinic" active={sortKey === 'clinic'} dir={sortDir} onSort={toggle} align="left" />
              </th>
              <th className="px-3 py-2 text-right font-medium">
                <SortHeader label="Fee/mo" sortKey="fee" active={sortKey === 'fee'} dir={sortDir} onSort={toggle} />
              </th>
              {data.months.map((m, mi) => (
                <th key={m} className="px-2.5 py-2 text-right font-medium whitespace-nowrap">
                  <SortHeader label={m} sortKey={`m${mi}`} active={sortKey === `m${mi}`} dir={sortDir} onSort={toggle} />
                </th>
              ))}
              <th className="px-5 py-2 text-right font-semibold text-stone-600 dark:text-stone-300">
                <SortHeader label="Total used" sortKey="total" active={sortKey === 'total'} dir={sortDir} onSort={toggle} />
              </th>
            </tr>
          </thead>
          <tbody>
            {clinics.map((c) => (
              <tr key={c.clinic} className="border-b border-stone-50 even:bg-stone-50/40 dark:border-stone-800/60 dark:even:bg-stone-800/20">
                <td className="sticky left-0 z-10 max-w-[200px] truncate bg-white px-5 py-2 text-stone-700 dark:bg-stone-900 dark:text-stone-200" title={c.clinic}>
                  {c.clinic}
                </td>
                <td className={cn('px-3 py-2 text-right text-stone-400 dark:text-stone-500', MONO)}>{c.fee ? fmt(c.fee, currency) : '—'}</td>
                {c.monthly.map((v, mi) => (
                  <td key={mi} className={cn('px-2.5 py-2 text-right', v ? '' : 'text-stone-300 dark:text-stone-600', MONO)}>
                    {v ? fmt(v, currency) : '·'}
                  </td>
                ))}
                <td className={cn('px-5 py-2 text-right font-semibold', MONO)}>{fmt(c.total, currency)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-stone-200 dark:border-stone-700">
              <td className="sticky left-0 z-10 bg-white px-5 py-2.5 font-semibold dark:bg-stone-900">Total credit used</td>
              <td />
              {data.months.map((_m, mi) => (
                <td key={mi} className={cn('px-2.5 py-2.5 text-right font-semibold', MONO)}>{fmt(monthTotal(mi), currency)}</td>
              ))}
              <td className={cn('px-5 py-2.5 text-right font-bold', MONO)}>{fmt(grand, currency)}</td>
            </tr>
          </tbody>
        </table>
      </div>
        </>
      )}
    </div>
  )
}
